import { type Report } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { type ReactNode, useState } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { chooseReportArtifact } from '@/features/routines/report-body';
import { type SpriteSummary } from '@/features/sprites/sprites.store';
import { useSpriteArtifact } from '@/features/sprites/use-sprite-artifact';
import { useSpriteForFile } from '@/features/sprites/use-sprite-for-file';
import { type HydratedFile, useHydratedFiles } from '@/hooks/useHydratedFile';
import { cn } from '@/lib/utils';

const SKELETON_LINE_WIDTHS = ['w-3/5', 'w-full', 'w-4/5'];

const Paper = ({
  paperRef,
  children,
}: {
  readonly paperRef?: (node: HTMLDivElement | null) => void;
  readonly children: ReactNode;
}) => (
  <div
    ref={paperRef}
    aria-hidden
    inert
    className="pointer-events-none relative h-[calc(30*var(--ac-u))] w-[calc(24*var(--ac-u))] shrink-0 rotate-[5deg] overflow-hidden rounded-[calc(3*var(--ac-u))] border border-grayA-3 bg-surface shadow-sm transition-transform duration-200 group-hover/feed-row:-translate-y-[calc(2*var(--ac-u))] group-hover/feed-row:rotate-[3deg] motion-reduce:transition-none motion-reduce:group-hover/feed-row:transform-none"
  >
    {children}
  </div>
);

const PaperLines = () => (
  <YStack className="gap-[calc(2*var(--ac-u))] p-[calc(4*var(--ac-u))]">
    {SKELETON_LINE_WIDTHS.map((width, index) => (
      <div
        key={index}
        className={cn('h-[calc(2*var(--ac-u))] rounded-full bg-grayA-4', width)}
      />
    ))}
  </YStack>
);

const SpriteThumb = observer(
  ({
    sprite,
    hydrated,
  }: {
    readonly sprite: SpriteSummary;
    readonly hydrated: HydratedFile;
  }) => {
    const { ref, display } = useSpriteArtifact(sprite, hydrated);
    const [brokenSrc, setBrokenSrc] = useState<string>();
    const src =
      display.kind === 'thumbnail'
        ? display.src
        : display.kind === 'image'
          ? display.content.url
          : null;

    return (
      <Paper paperRef={ref}>
        {src && src !== brokenSrc ? (
          <img
            src={src}
            alt=""
            draggable={false}
            onError={() => setBrokenSrc(src)}
            className="size-full object-cover object-left-top"
          />
        ) : (
          <PaperLines />
        )}
      </Paper>
    );
  }
);

export const HomeRoutineArtifactThumb = observer(
  ({ report }: { readonly report: Report }) => {
    const artifacts = useHydratedFiles(report.artifactFileIds);
    const choice = artifacts.some(({ status }) => status === 'loading')
      ? undefined
      : chooseReportArtifact(artifacts.map((artifact) => artifact.file));
    const sprite = useSpriteForFile(choice?.file?.id);

    if (!choice?.file) return null;
    return sprite ? (
      <SpriteThumb sprite={sprite} hydrated={artifacts[choice.index]} />
    ) : (
      <Paper>
        <PaperLines />
      </Paper>
    );
  }
);
